import React, { useState, useEffect } from 'react';
import styles from '../styles/DownloadHelper.module.css';

const DownloadHelper = ({ isVisible, onClose }) => {
  const [browserType, setBrowserType] = useState('');
  const [deviceType, setDeviceType] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [gifLoaded, setGifLoaded] = useState(false);

  // Detect browser and device on component mount
  useEffect(() => {
    // Simple browser detection
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf("Chrome") > -1) {
      setBrowserType('chrome');
    } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
      setBrowserType('safari');
    } else if (userAgent.indexOf("Firefox") > -1) {
      setBrowserType('firefox');
    } else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
      setBrowserType('ie');
    } else if (userAgent.indexOf("Edge") > -1) {
      setBrowserType('edge');
    }

    // Simple device detection
    if (/iPad|iPhone|iPod/.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      setDeviceType('ios');
    } else if (userAgent.indexOf("Android") > -1) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  // Function to get the appropriate demo GIF based on browser/device
  const getDownloadDemonstrationGif = () => {
    if (deviceType === 'ios') {
      return '/images/downloads/ios-download.gif';
    } else if (deviceType === 'android') {
      return '/images/downloads/android-download.gif';
    } else {
      // Desktop browsers
      switch (browserType) {
        case 'chrome':
          return '/downloads/chrome-download.gif';
        case 'firefox':
          return '/downloads/firefox-download.gif';
        case 'safari':
          return '/downloads/safari-download.gif';
        case 'edge':
          return '/downloads/edge-download.gif';
        default:
          // Fallback to Chrome if browser detection fails
          return '/downloads/chrome-download.gif';
      }
    }
  };

  if (!isVisible) return null;

  return (
    <div className={styles.downloadHelperOverlay}>
      <div className={styles.downloadHelperContent}>
        <button className={styles.closeButton} onClick={onClose}>×</button>
        
        <h2 className={styles.downloadTitle}>How to Download Your Song</h2>
        
        <div className={styles.downloadInstructions}>
          {/* Desktop Instructions */}
          {deviceType === 'desktop' && (
            <>
              <div className={styles.deviceDetection}>
                We detected you're using a computer with {browserType === 'chrome' ? 'Chrome' : 
                  browserType === 'firefox' ? 'Firefox' : 
                  browserType === 'safari' ? 'Safari' : 
                  browserType === 'edge' ? 'Microsoft Edge' : 'your browser'}
              </div>
              
              <div className={styles.instructions}>
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <h3>Right-click on the "Download MP3" button</h3>
                    <p>Position your mouse over the download button, then click your <strong>right</strong> mouse button (not the left button you normally use).</p>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <h3>Select {browserType === 'chrome' || browserType === 'edge' ? '"Save link as..."' : 
                      browserType === 'firefox' ? '"Save Link As..."' : 
                      browserType === 'safari' ? '"Download Linked File As..."' : '"Save link as..."'}</h3>
                    <p>A menu will appear. Click on the option that says {browserType === 'chrome' || browserType === 'edge' ? '"Save link as..."' : 
                      browserType === 'firefox' ? '"Save Link As..."' : 
                      browserType === 'safari' ? '"Download Linked File As..."' : '"Save link as..."'}</p>
                    
                    {/* Browser-specific demonstration GIF */}
                    <div className={styles.imageContainer}>
                      <img 
                        src={getDownloadDemonstrationGif()} 
                        alt={`How to download in ${browserType}`} 
                        className={styles.demoImage}
                        onLoad={() => setGifLoaded(true)}
                        onError={(e) => {
                          e.target.onerror = null; // Prevent infinite loop
                          e.target.src = "/images/downloads/default-download.gif";
                        }}
                      />
                      {!gifLoaded && <div className={styles.gifLoading}>Loading demonstration...</div>}
                    </div>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <h3>Choose where to save your song</h3>
                    <p>A window will open asking where you want to save the file. Select a location you'll remember, like your Downloads folder or Desktop.</p>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>4</div>
                  <div className={styles.stepContent}>
                    <h3>Click "Save"</h3>
                    <p>Your song will download to your computer in the location you selected.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* iOS Instructions */}
          {deviceType === 'ios' && (
            <>
              <div className={styles.deviceDetection}>
                We detected you're using an iPhone or iPad
              </div>
              
              <div className={styles.instructions}>
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <h3>Tap and hold on the "Download MP3" button</h3>
                    <p>Press your finger on the download button and hold it there for about 2 seconds.</p>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <h3>Select "Download Linked File" or "Save to Files"</h3>
                    <p>A menu will appear. Choose the option to download or save the file.</p>
                    
                    {/* iOS-specific demonstration GIF */}
                    <div className={styles.imageContainer}>
                      <img 
                        src={getDownloadDemonstrationGif()} 
                        alt="How to download on iOS" 
                        className={styles.demoImage}
                        onLoad={() => setGifLoaded(true)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/downloads/default-download.gif";
                        }}
                      />
                      {!gifLoaded && <div className={styles.gifLoading}>Loading demonstration...</div>}
                    </div>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <h3>Choose where to save</h3>
                    <p>Select a location like iCloud Drive or "On My iPhone/iPad".</p>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>4</div>
                  <div className={styles.stepContent}>
                    <h3>Find your song in the Files app</h3>
                    <p>Open your Files app to play your downloaded song.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Android Instructions */}
          {deviceType === 'android' && (
            <>
              <div className={styles.deviceDetection}>
                We detected you're using an Android device
              </div>
              
              <div className={styles.instructions}>
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>1</div>
                  <div className={styles.stepContent}>
                    <h3>Press and hold on the "Download MP3" button</h3>
                    <p>Touch and hold your finger on the download button for about 2 seconds.</p>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>2</div>
                  <div className={styles.stepContent}>
                    <h3>Select "Download link" or "Save link"</h3>
                    <p>A menu will appear with download options. Tap on the option to save the link.</p>
                    
                    {/* Android-specific demonstration GIF */}
                    <div className={styles.imageContainer}>
                      <img 
                        src={getDownloadDemonstrationGif()} 
                        alt="How to download on Android" 
                        className={styles.demoImage}
                        onLoad={() => setGifLoaded(true)}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/downloads/default-download.gif";
                        }}
                      />
                      {!gifLoaded && <div className={styles.gifLoading}>Loading demonstration...</div>}
                    </div>
                  </div>
                </div>
                
                <div className={styles.instructionStep}>
                  <div className={styles.stepNumber}>3</div>
                  <div className={styles.stepContent}>
                    <h3>Find your song in the Downloads folder</h3>
                    <p>Your song will be in your device's Downloads folder or in your Music app.</p>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Additional help section */}
          <div className={styles.additionalHelp}>
            <button 
              className={styles.toggleMoreOrange} 
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? "Show Less" : "Need More Help?"}
            </button>
            
            {showMore && (
              <div className={styles.moreHelp}>
                <h3>Additional Tips:</h3>
                <ul>
                  <li>Having trouble finding your downloaded song? Most computers store downloads in a folder called "Downloads".</li>
                  <li>If you need help playing your song, try double-clicking on the file after it's downloaded.</li>
                  <li>Want to save your song to a different device? You can email the song to yourself as an attachment.</li>
                  <li>Having trouble? Call us at 1-800-MELODY1 and we'll help you download your song!</li>
                </ul>
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.downloadButtons}>
          <button className={styles.gotItButton} onClick={onClose}>Got it, thanks!</button>
          <button className={styles.closeButtonSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default DownloadHelper;